import moment, { Moment } from "moment";
import { DataFormat, GroupedDataFormat, IdTextPair, LoadingData, PlainObject, QueryOptions } from "select2";
import { Formats } from "../../../TrifortCMS.Web.Common/Scripts/Common/formats";
import { IGridRenderer } from "../../../TrifortCMS.Web.Common/Scripts/interfaces";
import { HttpMethod, HttpService } from "../Common/http-service";
import { StringUtilities } from "../Common/string";
import { BootstrapHelpers } from "./bootstrap-helpers";
import { EmptyGuid } from "./constants";
import { CookieService } from "./cookie-helper";
import { FormService, ISelect2Item } from "./form-service";

export interface ISelectListItem {
    disabled: boolean;
    selected: boolean;
    text: string;
    value: string;
}

export interface ISelect2Opts {
    controlSelector: string;
    allowClear?: boolean;
    url?: string;
    addItemCallbackFn?: (item:any) => void;
    tags?: boolean;
    initialValue?: string;
    dataCallback?: ((params: QueryOptions) => PlainObject | string) | undefined;
    containerCssClass?: string; // Note this class is actually applied to the combobox two layers inside the container, obviously...
    dropdownAutoWidth?: boolean;
    dropdownParent?: JQuery<HTMLElement> | undefined;
    dropdownCssClass?: string;
    templateResult?: ((result: LoadingData | DataFormat | GroupedDataFormat) => string | JQuery | null) | undefined;
    templateSelection?: ((selection: IdTextPair | LoadingData, container: JQuery) => string | JQuery) | undefined;
}

interface IConfirmModalOpts {
    denyFn: Function;
    confirmBtnText: string;
    denyBtnText: string;
}

interface IMoveRowUpOrDownArgs {
    tableSelector: string;
    raise: boolean;
}

export type StringDictionaryEntry = {
    value: string;
    key: string;
};

export interface IDataTableRowMenuItem {
    text: string,
    cssClass?: string,
    dataItems?: StringDictionaryEntry[],
    attributes?: StringDictionaryEntry[],
    iconHtml?: string,
}

export function DataTables_NoRender(data: any, type: any, row: any, meta: any) {
    return '';
}

export function DataTables_RenderBool(data: any, type: any, row: any, meta: DataTables.CellMetaSettings) {
    return (data) ? '<i class="fas fa-check"></i>' : '<i class="fas fa-times"></i>';
}

export function DataTables_RenderDate(data: any, type: any, row: any, meta: DataTables.CellMetaSettings) {
    return ApiDateToDisplayedDate(data as string);
}

export function DataTables_RenderDecimalAsCurrency(data: any, type: any, row: any, meta: DataTables.CellMetaSettings) {
    if (data == null) {
        return '';
    }

    var formatter = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
    });

    return formatter.format(data);
}

export function ApiDateToDisplayedDate(apiDate: string | Date) : string {    
    if (apiDate == '' || apiDate == undefined) {
        return '';
    }

    if (typeof apiDate === 'string') {
        var date = new Date(apiDate);
        return ApiDateToDisplayedDate(date);
    }

    var date = apiDate as Date;

    if (!isValidDate(date)) {
        return '';
    }

    var mm = date.getMonth() + 1; // getMonth() is zero-based
    var dd = date.getDate();

    return [(dd > 9 ? '' : '0') + dd + "/",
    (mm > 9 ? '' : '0') + mm + "/",
    date.getFullYear(),
    ].join('');
}

export function isValidDate(dateInstance: Date) {
    return dateInstance instanceof Date;
}

export function DataTables_RenderDateTime(data: any, type: any, row: any, meta: DataTables.CellMetaSettings) {
    return ConvertFormattedDateTimeStringToEnglish(data);
}

export function ConvertFormattedDateTimeStringToEnglish(data: string | Date) : string {
    if (data == null) {
        return "";
    }

    if (typeof (data) == "string") {
        return ConvertFormattedDateTimeStringToEnglish(new Date(data));
    }

    var date = data as Date;

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString();

    return `${day}/${month}/${year} ${hours}:${minutes}`;
}

export function DataTables_RenderRowSelector(row: any, id: string) {

    return "<div class='d-flex justify-content-center'><input type='radio' class='rowSelectorRadio' name='rowSelectorRadio' data-id='" + id + "' data-orderno='" + row.sequence + "' ></div>";
}

export interface IDataTableInitOpts {
    emptyTable: string;
    gridSelector: string;
    url: string;
    ordering: boolean;
    sequence?: boolean;
    columns: DataTables.ColumnSettings[],
    columnDefs?: DataTables.ColumnDefsSettings[],
    modifyDataTableRequestDataFn?: Function,
    loadData?: any,
    pageSize?: number,
    order?: Array<(number | string)> | Array<Array<(number | string)>> | undefined;
}

export class Page {
    private static _redirectCookieName = "AssetTracker.Redirect";
    private static _bsDataPrefix = "data-";

    constructor() {
        var dismissableHelpSections = $("body").find('[data-dismiss-help]');

        dismissableHelpSections.on('click', function () {
            var idOfHelpSection = $(this).data('dismiss-help');
            $('#' + idOfHelpSection).addClass('d-none');
        });
    }

    public InitDataTable(opts: IDataTableInitOpts) : DataTables.Api {
        var self = this;

        // See if there is a search box which is connected to the grid
        var searchFilterId = $(opts.gridSelector).attr('id') + "SearchFilter";
        var searchFilter = $(`#${searchFilterId}`);

        if (searchFilter.length == 1) {
            // When the user changes the search filter, trigger the datatable to refresh.
            // Use a throttle to prevent hitting the endpoint too often
            var search = $.fn.dataTable.util.throttle(
                function (val) {
                    // Will only execute 1000ms after the last change
                    Page.refreshDataTable(opts.gridSelector);
                },
                1000
            );

            searchFilter.on('input', function () {
                search();
            });
        };

        var table = $(opts.gridSelector);
        var tableId = $(opts.gridSelector).attr('id');

        if (opts.sequence) {
            table = table.on('init.dt', function () {
                self.DetermineSequenceButtonEnabled(tableId);
            });
        }

        var api = $(opts.gridSelector).DataTable({
            'serverSide': true,
            "searching": false,
            "lengthChange": false,
            "pageLength": opts.pageSize ? opts.pageSize : 10,
            "destroy": true,
            "responsive": true,
            autoWidth: false,
            ordering: opts.ordering,
            columnDefs: opts.columnDefs,
            "order": opts.order || [0, 'asc'],
            "language": {
                emptyTable: opts.emptyTable
            },
            "ajax": {
                dataType: 'json',
                url: opts.url,
                contentType: 'application/json',
                type: 'GET',
                dataSrc: 'items',
                data: function (dataTableRequestData: any) {
                    var result = {
                        Page: (dataTableRequestData.start / dataTableRequestData.length) + 1,
                        PageSize: dataTableRequestData.length,
                        Draw: dataTableRequestData.draw,
                        Sort: dataTableRequestData.order,
                        Data: opts.loadData,
                        SearchFilter: '',
                    };

                    if (searchFilter.length == 1) {
                        result.SearchFilter = searchFilter.val() as string;
                    }

                    if (opts.modifyDataTableRequestDataFn != null) {
                        return opts.modifyDataTableRequestDataFn(result);
                    }

                    return result;
                }
            },
            "columns": opts.columns
        });

        if (opts.sequence === true) {
            $(opts.gridSelector).on('click', '.rowSelectorRadio', function () {
                self.DetermineSequenceButtonEnabled(tableId);
            });

            const raiseButton = $('div[data-sequence-for="' + tableId + '"] a.sequence-raise');
            raiseButton.on('click', async function () {
                self.RaiseOrLowerSequenceOfRowAsync({ tableSelector: opts.gridSelector, raise: true });
            });

            const lowerButton = $('div[data-sequence-for="' + tableId + '"] a.sequence-lower');
            lowerButton.on('click', async function () {
                self.RaiseOrLowerSequenceOfRowAsync({ tableSelector: opts.gridSelector, raise: false });
            });
        }

        return api;
    }

    public static CopyStringToClipboard(val: string, description: string = null) {
        navigator.clipboard.writeText(val);

        if (description != null) {
            toastr.success(description + ' Copied');
        }
    }

    public static CopyToClipboard(controlSelector: string, description: string = null) {
        return this.CopyStringToClipboard($(controlSelector).val() as string, description);
    }

    public static GetLoggedInUserId(): string {
        return $('#LoggedInUser_UserId').val() as string;
    }

    public static GetLoggedInOrganisationId(): string {
        return $('#LoggedInUser_OrganisationId').val() as string;
    }

    public static GetLoggedInOrganisationSettingsId(): string {
        return $('#LoggedInUser_OrganisationSettingsId').val() as string;
    }

    public static DataTableRowCount(controlSelector: string): number {
        return $(controlSelector).DataTable().rows().count();
    }

    public initialiseMultiSelect(controlSelector: string, nonSelectedText: string, opts: any = null) {
        opts = opts || {};

        opts.buttonWidth = opts.buttonWidth || '100%';
        opts.nonSelectedText = nonSelectedText || 'Check an option!';
        
        // Use bootstrap-multiselect component
        ($(controlSelector) as any).multiselect(opts);
    }

    
    public generateSelectListFromClassType<T>(type: T): ISelectListItem[] {
        const selectList: ISelectListItem[] = [];

        for (const key in type) {
            if (typeof type[key] === 'string') {
                const listItem: ISelectListItem = {
                    disabled: false,
                    selected: false,
                    text: StringUtilities.convertToSentenceCase(key),
                    value: (type as any)[key] as string,
                };

                selectList.push(listItem);
            }
        }

        return selectList;
    }

    // ---------------------------------------------------------------------------------------------
    // Initialises a select2 control - either based off a fixed list of items or a URL
    //
    // If a URL is specified the data will be retrieved from the endpoint.
    // ---------------------------------------------------------------------------------------------
    public async initialiseSelect2List(opts: ISelect2Opts) : Promise<JQuery<HTMLElement>> {
        var self = this;

        const control = $(opts.controlSelector);
        
        var select2Opts: Select2.Options = {
            allowClear: opts.allowClear ?? true,
            containerCssClass: opts.containerCssClass,
            dropdownAutoWidth: opts.dropdownAutoWidth ?? false,
            dropdownParent: opts.dropdownParent,
            theme: "bootstrap-5",
            placeholder: {
                id: "",
                text: "Not Selected"
            },
            tags: opts.tags,
            templateResult: opts.templateResult,
            templateSelection: opts.templateSelection,
            createTag: opts.tags ?
                function (params) {
                    var term = $.trim(params.term);

                    if (term === '') {
                        return null;
                    }
                    return {
                        id: term,
                        text: term,
                        newTag: true // add additional parameters
                    };
                } : null,
            width: null, // We deal with the width through css
        };

        if (opts.url) {
            select2Opts.ajax = {
                url: opts.url,
                dataType: 'json',
                data: opts.dataCallback,
            };
        }

        const select2 = control.select2(select2Opts)
        // These next 2 events prevent select2 from opening the list of items when the current one is cleared
          .on('select2:unselecting', function () {
            $(this).data('unselecting', true);
        }).on('select2:opening', function (e) {
            if ($(this).data('unselecting')) {
                $(this).removeData('unselecting');
                e.preventDefault();
            }
        });

        if (opts.tags){
            select2.on('select2:select', function (e) {
                var selectedItem = $(this).select2('data');
                if (selectedItem != null && selectedItem.length == 1 && (selectedItem[0] as any).newTag == true) {
                    opts.addItemCallbackFn(selectedItem[0]);
                }
            });
        }

        var isMultiple = control.prop('multiple');
        if (isMultiple) {
            // Multi select
            if (opts.url) {
                // Not supported for now, write it when we need it
            } else {
                // Fixed list of options
            }
        }
        else {
            // Single select
            let initialSelectedId = control.attr('value');
            if (opts.hasOwnProperty('initialValue')) {
                initialSelectedId = opts.initialValue;
            }

            if (initialSelectedId != null && initialSelectedId != EmptyGuid) {
                const httpResponse = await HttpService.callHttpService({
                    Method: HttpMethod.GET,
                    Url: opts.url + "?ids=" + initialSelectedId + "&allowGrouping=false",
                    SupressSuccessMessage: true,
                });

                if (httpResponse.Ok) {
                    if (httpResponse.Data != null) {
                        var items = (httpResponse.Data as any).results as Array<ISelect2Item>;
                        if (items != null && items.length == 1) {
                            var select2Item = items[0]; //self.ConvertSelectListItemToSelect2DataItem(items[0]);
                            select2Item.selected = true;
                            FormService.addOptionToSelect2(opts.controlSelector, select2Item);
                        }
                    }
                }
            } else {
                FormService.clearSelectedOptionsFromSelect2(opts.controlSelector);
            }
        }


        return Promise.resolve(select2);
    }

    public getSelect2SelectedData(controlSelector: string): any {
        return $(controlSelector).select2('data');
    }

    public async repopulateSelectList(controlSelector: string, url: string, httpData?: any, dontOverwriteOptions?: boolean) {
        if (dontOverwriteOptions && $(`${controlSelector} option`).length > 0) 
            return;
        
        var httpResponse = await HttpService.callHttpService({
            Method: HttpMethod.GET,
            Url: url,
            Data: httpData,
            SupressSuccessMessage: true,
        });

        if (httpResponse.Ok) {
            var loadedData = httpResponse.Data as ISelectListItem[];

            this.repopulateSelectListFromData(controlSelector, loadedData);
        }
    }

    public repopulateSelectListFromData(controlSelector: string, data: ISelectListItem[]) {
        if (this.isMultiSelect(controlSelector)) {
            return this.repopulateMultiSelectListFromData(controlSelector, data);
        }
        
        var selectElem = $(controlSelector);

        // If there is an existing option selected, store it before we clear out the control
        var existingSelectedItemValue = selectElem.children("option:selected").val();

        // Clear out existing options
        selectElem.children().remove();
        // Add new options to the select list

        // If there is no existing selected option and we have some select list items returned, select the first one
        if (existingSelectedItemValue == null && data.length > 0) {
            existingSelectedItemValue = data[0].value;
        }

        var optionsHtml = '';
        data.map(function (item) {
            var selected = '';

            if (item.value == existingSelectedItemValue) {
                selected = ' selected'
            }

            optionsHtml = optionsHtml + '<option value="' + item.value + '"' + selected + '>' + item.text + '</option>';
        });

        selectElem.html(optionsHtml);
    }

    public initDateRangePicker(dateRangeSelector: string, start: Moment, end: Moment, maxSpan?: number, gridRendererToRefresh?: IGridRenderer) {
        var self = this;
        function cb(start: Moment, end: Moment) {
            $(`${dateRangeSelector} span`).html(start.format('MMM D, YYYY') + ' - ' + end.format('MMM D, YYYY'));
        }

        $(dateRangeSelector).daterangepicker({
            startDate: start,
            endDate: end,
            maxSpan: {
                "days": maxSpan ?? 31
            },
            ranges: {
                'Today': [moment(), moment()],
                'Yesterday': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
                'Last 7 Days': [moment().subtract(6, 'days'), moment()],
                'Last 30 Days': [moment().subtract(29, 'days'), moment()],
                'This Month': [moment().startOf('month'), moment().endOf('month')],
                'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')]
            },
        }, cb);
        
        cb(start, end);
    }

    public getAddDateRangeToJsonRequestFunc(dateRangeSelector: string): (jsonData: string) => void {
        return (jsonData: any) => {
            // Get the currently selected daterange 
            var dateRangePickerData = $(dateRangeSelector).data('daterangepicker');
            if (dateRangePickerData != null && dateRangePickerData.startDate != null && dateRangePickerData.endDate != null) {
                var startDate = dateRangePickerData.startDate.format(Formats.MomentDateFormatApi);
                var endDate = dateRangePickerData.endDate.format(Formats.MomentDateFormatApi);

                jsonData.Filters['StartDate'] = { operator: 'gteq', value: startDate };
                jsonData.Filters['EndDate'] = { operator: 'lteq', value: endDate };
            }
        }
    }

    private repopulateMultiSelectListFromData(controlSelector: string, data: ISelectListItem[]) {
        var self = this;
        if (FormService.isSelect2(controlSelector)) {
            // Clear out existing items
            FormService.clearOptionsFromSelect2(controlSelector);

            // Add new items
            var items = data.map(function (item) {
                return {
                    id: item.value,
                    text: item.text,
                    selected: item.selected
                } as ISelect2Item;
            });

            FormService.addOptionsToSelect2(controlSelector, items);
        }
        else {
            // Probably boostrap multiselect
            var selectElem = $(controlSelector);

            // If there is an existing options selected, store them before we clear out the control
            var existingSelectedItems = self.getMultiSelectSelectedValues(controlSelector);

            // Clear out existing options
            selectElem.children().remove();
            (selectElem as any).multiselect('rebuild');

            // Add new options to the select list
            var optionsHtml = '';
            data.map(function (item) {
                optionsHtml = optionsHtml + '<option value="' + item.value + '">' + item.text + '</option>';
            });

            selectElem.html(optionsHtml);

            // Refresh the plugin so the updates are rendered
            (selectElem as any).multiselect('rebuild');
            (selectElem as any).multiselect('select', existingSelectedItems);
        }
    }

    // ----------------------------------------------------------------
    // Should be called when a tabbed page is first shown
    //
    // Sets up things such as the ability to track the currently shown tab, so it can be navigated back to between postbacks
    // ----------------------------------------------------------------
    public static initialiseTabbedPage() {
        // Wire up events
        $('.nav-tabs a').on('shown.bs.tab', function (e) {
            var anchorElement = e.target as HTMLAnchorElement;
            window.location.hash = anchorElement.hash;

            // Previous line can cause us to scroll down the page, so scroll back up
            $(window).scrollTop(0);

            // If the tab we're showing has tabs underneath and none are active, show the first one
            var selectorForTabWereShowing = $(this).attr('href');

            var subTabLinks = $(selectorForTabWereShowing + ' .nav-tabs a');
            if (subTabLinks.length > 0) {
                var activeSubTabLinks = $(selectorForTabWereShowing + ' .nav-tabs a.active');
                if (activeSubTabLinks.length == 0) {
                    BootstrapHelpers.ShowTab(subTabLinks[0]);
                }
            }

            CookieService.setCookie('AssetTracker.breadcrumbs', anchorElement.hash);
        });
    }

    public static setupTab(tabSelector: string, initFn: () => void) {
        $(tabSelector).on('shown.bs.tab', function () {
            // Is this the first time
            var alreadyActivated = $(this).data('already-activated');

            // If this is the first time this tab has been activated, call the initialisation function
            if (alreadyActivated == null) {
                $(this).data('already-activated', 'true');

                initFn();
            }
        });
    }

    public GetMultiPartFormData(selectors: string[]): FormData {
        var result = new FormData();

        for (var i = 0; i < selectors.length; i++) {
            var selector = selectors[i];
            var inputElement = $(selector);

            var id = inputElement.attr('id');
            var name = inputElement.attr('name');
            if (name == null) {
                name = id;
            }

            if (inputElement.attr('type') == 'file') {
                // File input
                var fileInputElement = inputElement[0] as HTMLInputElement;
                if (fileInputElement.files.length == 1) {
                    var file = fileInputElement.files[0];
                    result.append(name, file, file.name);
                }
            }
            else {
                // Normal input
                result.append(name, $(selector).val() as string);
            }
        }

        return result;
    }

    public static dateToYMD(date: Date): string {
        if (date == null) {
            return "";
        }

        var d = date.getDate();
        var m = date.getMonth() + 1; //Month from 0 to 11
        var y = date.getFullYear();
        return '' + y + '/' + (m <= 9 ? '0' + m : m) + '/' + (d <= 9 ? '0' + d : d);
    }

    public static getDateObjFromControl(controlSelector: string): Date {

        var controlVal = $(controlSelector).val().toString();

        if (controlVal == "") {
            return null;
        }

        return new Date(controlVal);
    }

    public static setChecked(controlSelector: string, checked: boolean) {
        $(controlSelector).prop('checked', checked);
    }

    public static getChecked(controlSelector: string): boolean {
        return $(controlSelector).is(':checked');
    }

    public static setSelectedItem(controlSelector: string, itemIds: string | string[]) {
        $(controlSelector).val(itemIds).trigger("change");
    }

    public static updateDateControlFromDateStr(controlSelector: string, dateStr: string) {
        var date = new Date(dateStr);

        var d = date.getDate();
        var m = date.getMonth() + 1; //Month from 0 to 11
        var y = date.getFullYear();

        $(controlSelector).val('' + y + '-' + (m <= 9 ? '0' + m : m) + '-' + (d <= 9 ? '0' + d : d))
    }

    public static getDateStringForPostFromControl(controlSelector: string): string {
        var dateObj = Page.getDateObjFromControl(controlSelector);

        return Page.dateToYMD(dateObj);
    }

    /// <summary>
    /// Gets the datetime string in the correct format for a datetime input control.
    /// </summary>
    public static getDateTimeStringForDateTimeInput(dateTime: Date) {
        return FormService.formatDateTimeToISO(dateTime);
    }

    /// <summary>
    /// Gets the date string in the correct format for a date input control.
    /// </summary>
    public static getDateStringForDateInput(dateTime: Date) {
        const year = dateTime.getFullYear();
        const month = (dateTime.getMonth() + 1).toString().padStart(2, '0');
        const day = dateTime.getDate().toString().padStart(2, '0');

        const formattedDateString = `${year}-${month}-${day}`;
        return formattedDateString;
    }

    public static ShowActiveTabOnLoad() {
        var hash = window.location.hash;
        if (hash.length > 0) {
            // This tab might be a child of another tab which is also not shown.
            var tabToShow = $('.nav-tabs a[href="' + hash + '"]');
            var parentTabSelector = tabToShow.data('parent-tab');
            if ($(parentTabSelector).length == 1) {
                BootstrapHelpers.ShowTab($(parentTabSelector)[0]);
            }

            // Show the child tab too
            BootstrapHelpers.ShowTab(tabToShow[0]);
        } else {
            // Show the first tab
            var $tabs = $('.nav-tabs a[data-toggle="tab"]');

            if ($tabs.length > 0) {
                BootstrapHelpers.ShowTab($tabs[0]);
	        }
        }
    }

    public static reload() {
        location.reload();
    }

    public static refreshDataTable(gridSelector: string, callbackFn: (json: any) => void = null) {
        var grid = $(gridSelector);
        if (grid.length > 0) {
            $(gridSelector).DataTable().ajax.reload(callbackFn);
        }
    }

    public GetFormData(selectors: string[]): object {
        var result: any = {
        };

        for (var i = 0; i < selectors.length; i++) {
            var selector = selectors[i];

            var $control = $(selector);

            if ($control.length != 1) {
                console.error(selector + ' did not reference 1 control');
                break;
            }

            var controlIdentifier = $control.attr('name') || $control.attr('id');

            if (controlIdentifier == null || controlIdentifier.length == 0) {
                console.error(selector + 'does not have a name');
                break;
            }

            var type = $control.attr('type') || ($control[0] as any).type;

            if (type == 'select-multiple') {
                result[controlIdentifier] = this.getMultiSelectSelectedValues(selector);
            }
            else if (type == 'datetime-local' && value == '') {
                // Dont' send null datetimes
            }
            else {
                var value = $control.val();

                if (type == 'checkbox') {
                    value = $(selector).is(':checked') ? 'true' : 'false';
                }

                result[controlIdentifier] = value;
            }
        }

        return result;
    }

    public getMultiSelectSelectedValues(selector: string): any[] {
        var selectedOptions = $(selector + ' option:selected');
        var selectedOptionsArray: any[] = [];

        selectedOptions.each(function (index, option) {
            selectedOptionsArray.push((option as any).value);
        });

        return selectedOptionsArray;
    }

    public isMultiSelect(controlSelector: string) {
        var $control = $(controlSelector);

        var type = $control.attr('type') || ($control[0] as any).type;

        return (type == 'select-multiple');
    }

    public selectItemInMultiSelect(controlSelector: string, selectedValue: string | string[]) {
        var selectElem = $(controlSelector);

        if (selectedValue == null) {
            $('option', selectElem).each(function (element) {
                (selectElem as any).multiselect('deselect', $(this).val());
            });
        }
        else {
            (selectElem as any).multiselect('select', selectedValue);
        }
    }

    public static displayNotification(headerText: string, bodyText: string, confirmFn: Function = null) {
        var modal = $('#NotificationModal');

        modal.unbind("show.bs.modal").on('show.bs.modal', function () {
            modal.find('.modal-title').text(headerText);
            modal.find('.modal-body').text(bodyText);
        });

        if (confirmFn != null) {
            modal.unbind("hidden.bs.modal").on('hidden.bs.modal', function () {
                confirmFn();
            });
        }

        modal.modal('show');
    }

    // -----------------------------------------------------------------------------
    // Raises a modal confirmation dialog asking the user for permission to perform
    // some action
    // 
    // headerText -> displayed in the modal header
    // bodyText -> displayed in the modal body
    // confirmFn -> function which is called if the user clicks to confirm
    // opts -> optional arguments
    //    .denyFn -> function called if the user clicks to deny permission
    //    .confirmBtnText -> defaulted to 'Yes', specifies the text on the confirm button
    //    .denyBtnText -> defaulted to 'No', specifies the text on the deny bytton
    // -----------------------------------------------------------------------------
    public static askForConfirmation(headerText: string, bodyText: string, confirmFn: Function, opts: IConfirmModalOpts) {
        var modal = $('#ConfirmationModal');

        if (opts == null) {
            opts = {
                denyBtnText: null,
                denyFn: null,
                confirmBtnText: null
            };
        }

        modal.unbind("show.bs.modal").on('show.bs.modal', function () {
            modal.find('.modal-title').text(headerText);
            modal.find('.modal-body').text(bodyText);

            if (opts.confirmBtnText) {
                modal.find('#Yes').text(opts.confirmBtnText);
            }

            if (opts.denyBtnText) {
                modal.find('#No').text(opts.denyBtnText);
            }

            modal.find('#Yes').unbind("click").on('click', function () {
                confirmFn();
            });

            modal.find('#No').unbind("click").on('click', function () {
                if (opts.denyFn) {
                    opts.denyFn();
                }
            });
        });

        modal.modal('show');
    }

    public static ClearFileInput(inputId: string, labelText?: string) {
        // Clear the label
        $('label[for="' + inputId + '"]').html(labelText);

        // Clear the actual input
        var inputElement = $('#' + inputId)[0] as HTMLInputElement;
        inputElement.value = '';
    }

    public static ValidateSize(fileInput: HTMLInputElement, maxSizeBytes: number, maxSizeDesc: string): boolean {
        var FileSize = fileInput.files[0].size;
        if (FileSize > maxSizeBytes) {
            toastr.error('File size exceeds maximum(' + maxSizeDesc + ')');

            // Clear the file input
            this.ClearFileInput($(fileInput).attr('id'));

            return false;
        }

        return true;
    }

    // 
    public static toggleClass(element: HTMLElement, ...cssClasses: string[]) {
        for (var i = 0; i < cssClasses.length; i++) {
            element.classList.toggle(cssClasses[i]);
        }
    }

    public async ensureLoggedOut(returnUrl: string) {
        if (await this.isLoggedIn()) {
            CookieService.setCookie(Page._redirectCookieName, returnUrl);

            await this.logOut();
        }
    }

    public async isLoggedIn(): Promise<boolean> {

        var httpResponse = await HttpService.callHttpService({
            Method: HttpMethod.GET,
            SupressSuccessMessage: true,
            Url: '/account/isloggedin',
        });

        if (httpResponse.Ok) {
            var responseData = httpResponse.Data as boolean;

            return responseData;
        }

        return false;
    }

    public redirectTo(url: string, delayMs?: number) {
        var self = this;

        if (delayMs != null) {
            // Wait for the specified number of milliseconds before redirecting
            setTimeout(function () {
                self.redirectTo(url);
            }, delayMs);
        }
        else {
            location.href = url;
        }
    }

    public async logOut() {
        if (await this.isLoggedIn()) {
            document.location.href = '/account/logout';
        }
    }

    public static redirectOnLoad() {
        var redirectCookie = CookieService.getCookie(Page._redirectCookieName);
        if (redirectCookie != null) {
            // Make sure we don't redirect again
            CookieService.deleteCookie(Page._redirectCookieName);

            window.location.href = redirectCookie;
        }
    }

    protected GetBooleanFromField(controlSelector: string): boolean {
        var $el = $(controlSelector);

        var type = $el.attr('type');

        switch (type) {
            case 'checkbox':
                return $el.attr('checked') == 'True';
            case 'radio':
                return $el.attr('checked') == 'True';
            default:
                return $el.val() == 'True';
        }
    }

    protected isBlankKeyValue(keyValue: string): boolean {
        return keyValue == EmptyGuid || keyValue == "0";
    }

    protected disableForm(formSelector: string) {
        var inputs = $(formSelector + ' input');

        inputs.prop('disabled', true);
    }

    public disableControl(controlSelector: string) {
        $(controlSelector).attr('disabled', 'disabled');
    }

    public enableControl(controlSelector: string) {
        $(controlSelector).removeAttr('disabled');
    }
    
    protected hideControl(controlSelector: string) {
        $(controlSelector).hide();
    }

    protected populateFormFromData(formSelector: string, data: any) {

        $.each(data, function (name: string, val) {
            name = StringUtilities.capitaliseFirstChar(name);
            var $el = $(formSelector + ' [name="' + name + '"]'),
                type = $el.attr('type');

            switch (type) {
                case 'checkbox':
                    $el.attr('checked', 'checked');
                    break;
                case 'radio':
                    $el.filter('[value="' + val + '"]').attr('checked', 'checked');
                    break;
                default:
                    $el.val(val);
            }
        });
    }

    protected async populateFormAsync(url: string, formSelector: string) {
        var self = this;

        var httpResponse = await HttpService.callHttpService({
            Url: url,
            SupressSuccessMessage: true,
            Method: HttpMethod.GET,
        });

        if (httpResponse.Ok) {
            var httpData = httpResponse.Data as any;

            self.populateFormFromData(formSelector, httpData);
        }
    }

    protected async clearForm(formSelector: string) {
        var inputs = $(formSelector + ' input');

        inputs.val('');
    }


    public GetDataTableRowDropdown(label:string, menuItems: IDataTableRowMenuItem[]): string {
        if (menuItems.length == 0) {
            return '';
        }

        var result = '<div class="dropdown">'
        result += '<button class="btn btn-primary btn-sm dropdown-toggle" type="button" '+ Page._bsDataPrefix + 'toggle="dropdown" aria-haspopup="true" aria-expanded="false">';
        result += label;
        result += '</button><div class="dropdown-menu dropdown-menu-right">';

        for (var menuItem of menuItems) {
            var cssClass = menuItem.cssClass || '';

            var dataStr = '';
            if (menuItem.dataItems != null) {
                menuItem.dataItems.forEach(function (dictionaryItem: StringDictionaryEntry) {
                    dataStr += `data-${dictionaryItem.key}="${dictionaryItem.value}"`;
                });
            }

            var attributesStr = '';

            if (menuItem.attributes != null) {
                menuItem.attributes.forEach(function (dictionaryItem: StringDictionaryEntry) {
                    attributesStr += `${dictionaryItem.key}="${dictionaryItem.value}"`;
                });
            }

            var iconHtml = menuItem.iconHtml || '';

            result += `<a href='#' class="dropdown-item ${cssClass}" ${dataStr} ${attributesStr}>${iconHtml} ${menuItem.text}</a>`;
        }

        result += '</div></div>';

        return result;
    }

    private async RaiseOrLowerSequenceOfRowAsync(opts: IMoveRowUpOrDownArgs) {
        var self = this;

        const selectedRowRadio = $(opts.tableSelector + ' input.rowSelectorRadio:checked');
        const selectedObjectId = selectedRowRadio.data('id');

        var tableId = $(opts.tableSelector).attr('id');
        const controllerName = tableId.replace(/Table$/, '');
        const actionName = opts.raise ? 'RaiseSequence' : 'LowerSequence';

        const url = '/' + controllerName + '/' + actionName + '/' + selectedObjectId;

        const httpResponse = await HttpService.getDataAsync(url, null);

        if (!httpResponse.Ok || !httpResponse.Data)
            toastr.error("Changing Sequence Number failed. Please reload and try again.")
        else {
            await Page.refreshDataTable(opts.tableSelector, function () {
                // Try to recheck the same row as before
                $(opts.tableSelector + ' input.rowSelectorRadio[data-id="' + selectedObjectId + '"]').attr('checked', 'true');

                // Recompute the up/down arrows
                self.DetermineSequenceButtonEnabled(tableId);
            });
        }
    }

    private DetermineSequenceButtonEnabled(tableId: string) {
        var tableSelector = '#' + tableId;

        const raiseButton = $('div[data-sequence-for="' + tableId + '"] a.sequence-raise');
        const lowerButton = $('div[data-sequence-for="' + tableId + '"] a.sequence-lower');

        var selectedRow = $(tableSelector + ' input.rowSelectorRadio:checked');

        if (selectedRow.length == 0) {
            // No row selected
            raiseButton.addClass('disabled');
            lowerButton.addClass('disabled');
        }
        else {
            const dataTable = $(tableSelector).DataTable();

            var sequenceNumberSelected = selectedRow.data('orderno');
            var totalRows = dataTable.settings()[0].json.recordsTotal;

            // Sequence Number goes from 0..N and should be contiguous
            if (sequenceNumberSelected < totalRows - 1) {
                lowerButton.removeClass('disabled');
            } else {
                lowerButton.addClass('disabled');
            }

            if (sequenceNumberSelected > 0) {
                raiseButton.removeClass('disabled');
            } else {
                raiseButton.addClass('disabled');
            }
        }
    }
}
